import React, {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
} from 'react';
import { toast } from 'react-toastify';
import styledComponent from '../../utils/styledComponent';
import theme from '../../theme';
import Tabs from '../../components/atoms/Tabs';
import FilterBar from '../../components/FilterBar';
import SingleSelect from '../../components/atoms/forms/SingleSelect';
import MultiSelect from '../../components/atoms/forms/MultiSelect';
import DateRangeField from '../../components/atoms/forms/DateRangeField';
import { OpsLocations } from '../../libs/GraphQL';
import RouteReviewTab from './RouteReviewTab';
import PlanningTab from './PlanningTab';
import DayBeforeTab from './DayBeforeTab';
import DayOfTab from './DayOfTab';
import ExceptionReviewTab from './ExceptionReviewTab';
import {
  TAB_IDS,
  TABS,
  REGIONS,
  REGION_ALL,
  DATE_RANGE_LIMIT_DAYS,
  DEFAULT_DATE_RANGE_DAYS,
} from './constants';
import { validateRange, getDefaultDateRange } from './utils';

const toastOpts = {
  className: 'prettyToast',
  position: 'top-center',
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  theme: 'light',
};

const StyledDiv = styledComponent('div', {
  '& .sd-container': {
    padding: theme.spacing.pagePadding,
  },
  '& .sd-page-header': {
    ...theme.typography.pageTitle,
    color: theme.colors.textDark,
    marginBottom: theme.spacing.pageTitleMargin,
  },
  '& .sd-tabs-row': {
    marginBottom: theme.spacing.sectionGap,
  },
  '& .sd-filter-row-wrapper': {
    marginBottom: theme.spacing.sectionGap,
  },
  '& .sd-input-wrapper': {
    display: 'flex',
    flexDirection: 'column',
    minWidth: theme.sizes.filterFieldMinWidth,
  },
  '& .sd-region-wrapper': {
    width: theme.sizes.regionSelectWidth,
  },
  '& .sd-market-wrapper': {
    width: theme.sizes.marketSelectWidth,
  },
  '& .sd-input-label': {
    ...theme.typography.label,
    color: theme.colors.text,
    marginBottom: theme.spacing.inputLabelGap,
  },
  [`@media (max-width: ${theme.breakpoints.mobile})`]: {
    '& .sd-container': {
      padding: `${theme.spacing.bodyPadding} 0`,
    },
    '& .sd-input-wrapper': {
      minWidth: 0,
      width: '100%',
    },
    '& .sd-region-wrapper, & .sd-market-wrapper': {
      width: '100%',
    },
  },
});

const REGION_DEFAULT = REGIONS.find((r) => r.value === REGION_ALL);

function buildDefaultFilters() {
  const { startDate, endDate } = getDefaultDateRange(DEFAULT_DATE_RANGE_DAYS);
  return {
    startDate,
    endDate,
    region: REGION_DEFAULT,
    market: [],
  };
}

function filtersEqual(a, b) {
  return (
    a.startDate.getTime() === b.startDate.getTime() &&
    a.endDate.getTime() === b.endDate.getTime() &&
    a.region.value === b.region.value &&
    a.market.length === b.market.length &&
    a.market.every((m, i) => m.value === b.market[i].value)
  );
}

function SupervisorDashboard() {
  const [activeTab, setActiveTab] = useState(TAB_IDS.ROUTE_REVIEW);
  const [draftFilters, setDraftFilters] = useState(buildDefaultFilters);
  const [committedFilters, setCommittedFilters] = useState(buildDefaultFilters);
  const [fcOptions, setFcOptions] = useState([]);
  const [fcByLocationId, setFcByLocationId] = useState({});
  const [isFcLoading, setIsFcLoading] = useState(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    const fetchFcs = async () => {
      setIsFcLoading(true);
      try {
        const allLocations = [];
        let nextToken = null;
        do {
          // eslint-disable-next-line no-await-in-loop
          const response = await OpsLocations({
            type: 'FRIDGE',
            nextToken,
          });
          if (!mountedRef.current) return;
          const locations = response?.data?.opsLocations?.locations || [];
          allLocations.push(...locations);
          nextToken = response?.data?.opsLocations?.nextToken;
        } while (nextToken);

        const idToFc = {};
        const uniqueFcs = new Set();
        allLocations.forEach((loc) => {
          if (
            loc.id &&
            loc.fulfillmentCenter &&
            loc.fulfillmentCenter !== 'Does Not Exist'
          ) {
            idToFc[loc.id] = loc.fulfillmentCenter;
            uniqueFcs.add(loc.fulfillmentCenter);
          }
        });
        const opts = [...uniqueFcs]
          .sort((a, b) => a.localeCompare(b))
          .map((fc) => ({ value: fc, label: fc }));
        if (!mountedRef.current) return;
        setFcByLocationId(idToFc);
        setFcOptions(opts);
      } catch (e) {
        if (!mountedRef.current) return;
        console.error('SupervisorDashboard FC fetch failed:', e);
        toast.error(
          'Failed fetching fulfillment centers. Please try again.',
          toastOpts,
        );
      } finally {
        if (mountedRef.current) {
          setIsFcLoading(false);
        }
      }
    };
    fetchFcs();
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const validation = useMemo(
    () =>
      validateRange(
        draftFilters.startDate,
        draftFilters.endDate,
        DATE_RANGE_LIMIT_DAYS,
      ),
    [draftFilters.startDate, draftFilters.endDate],
  );

  const isDirty = useMemo(
    () => !filtersEqual(draftFilters, committedFilters),
    [draftFilters, committedFilters],
  );

  const applyDisabled = !isDirty || !validation.valid;

  const handleStartChange = useCallback((startDate) => {
    setDraftFilters((prev) => ({ ...prev, startDate }));
  }, []);

  const handleEndChange = useCallback((endDate) => {
    setDraftFilters((prev) => ({ ...prev, endDate }));
  }, []);

  const handleRegionChange = useCallback((region) => {
    setDraftFilters((prev) => ({ ...prev, region }));
  }, []);

  const handleMarketChange = useCallback((market) => {
    setDraftFilters((prev) => ({ ...prev, market: market || [] }));
  }, []);

  const handleApply = useCallback(() => {
    if (!validation.valid) return;
    setCommittedFilters(draftFilters);
  }, [draftFilters, validation.valid]);

  const handleClearAll = useCallback(() => {
    const defaults = buildDefaultFilters();
    setDraftFilters(defaults);
    setCommittedFilters(defaults);
  }, []);

  const renderActiveTab = () => {
    switch (activeTab) {
      case TAB_IDS.ROUTE_REVIEW:
        return (
          <RouteReviewTab
            filters={committedFilters}
            fcByLocationId={fcByLocationId}
          />
        );
      case TAB_IDS.PLANNING:
        return <PlanningTab />;
      case TAB_IDS.DAY_BEFORE:
        return <DayBeforeTab />;
      case TAB_IDS.DAY_OF:
        return <DayOfTab />;
      case TAB_IDS.EXCEPTION_REVIEW:
        return <ExceptionReviewTab />;
      default:
        return null;
    }
  };

  return (
    <StyledDiv>
      <div className="sd-container">
        <h1 className="sd-page-header">Supervisor Dashboard</h1>

        <div className="sd-tabs-row">
          <Tabs tabs={TABS} activeId={activeTab} onChange={setActiveTab} />
        </div>

        <div className="sd-filter-row-wrapper">
          <FilterBar
            onApply={handleApply}
            onClearAll={handleClearAll}
            applyDisabled={applyDisabled}
          >
            <div className="sd-input-wrapper sd-region-wrapper">
              <div className="sd-input-label">Region</div>
              <SingleSelect
                options={REGIONS}
                value={draftFilters.region}
                onChange={handleRegionChange}
                width="100%"
              />
            </div>
            <div className="sd-input-wrapper sd-market-wrapper">
              <div className="sd-input-label">Market</div>
              <MultiSelect
                options={fcOptions}
                value={draftFilters.market}
                onChange={handleMarketChange}
                isLoading={isFcLoading}
                isDisabled={isFcLoading}
                getOptionLabel={(o) => o.label}
                getOptionValue={(o) => o.value}
                maxMenuHeight={400}
                width="100%"
              />
            </div>
            <div className="sd-input-wrapper">
              <div className="sd-input-label">Date Range</div>
              <DateRangeField
                startValue={draftFilters.startDate}
                endValue={draftFilters.endDate}
                onStartChange={handleStartChange}
                onEndChange={handleEndChange}
                error={validation.valid ? '' : validation.error}
              />
            </div>
          </FilterBar>
        </div>

        {renderActiveTab()}
      </div>
    </StyledDiv>
  );
}

export default SupervisorDashboard;
